import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import * as _moment from 'moment';
import { default as _rollupMoment, Moment } from 'moment';
import { EDUCATION_LEVEL_LABEL_KEYS, Option, SELECTABLE_EDUCATION_LEVELS } from '@digilize/shared/definitions/src';
import { TranslateService } from '@ngx-translate/core';
import { FormSectionService } from '../../services/form-section.service';
import { ErrorService } from '../../services/error.service';
import { MONTHS_FORMAT } from '../../consts/month-year-format';
import { createOptions } from '../../helpers/helpers';
import { EducationService } from '../../services/education.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

const moment = _rollupMoment || _moment;

@Component({
  selector: 'bwc-education-stage',
  templateUrl: './education-stage.component.html',
  styleUrls: ['./education-stage.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MONTHS_FORMAT },
  ],
})
export class EducationStageComponent implements OnInit {
  @Input() form: FormGroup;
  @Input() maxEducationSteps;
  @Input() editing = false;
  @Output() formValidityChanged = new EventEmitter<boolean>();

  isAddEducationDisabled = false;
  date = new FormControl(moment());
  levelOptions = [];
  studyOptions = [];
  countriesOptions: Option[] = [];
  dataLoaded = false;
  itemClickable = true;
  exp_index: number;
  exp_opened = false;
  optionJustClicked = false;

  constructor(
    private formSection: FormSectionService,
    public errorService: ErrorService,
    private educationService: EducationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      console.log(this.form);
      this.formValidityChanged.emit(this.form.valid);
    });
    this.loadCountries();
    this.initLevelOptions();
    this.dataLoaded = true;
    setTimeout(() => this.form.updateValueAndValidity(), 0);
  }

  private initLevelOptions(): void {
    for (let i = 0; i < this.formSections.length; i++) {
      this.levelOptions[i] = [...this.hardcodedLevelOptions];
    }
  }

  private loadCountries(): void {
    const lang = this.translateService.getDefaultLang();
    this.educationService.getCountriesOptions(lang).subscribe((options) => {
      this.countriesOptions = options;
    });
  }

  addEducation() {
    this.exp_index = undefined;
    setTimeout(() => (this.exp_opened = false), 350);

    const newFormGroup = this.formSection.createEducationFormSection({
      country: '',
      school_name: '',
      education_name: '',
      education_level: null,
      start_date: '',
      end_date: '',
      is_current: false,
      order: this.formSections.length,
    });
    this.formSections.push(newFormGroup);
    this.checkSectionCount();
    this.resetOrderProperty();
  }

  handleFilteredOptionClick(option: Option, index, name) {
    this.optionJustClicked = true;
    setTimeout(() => {
      this.optionJustClicked = false;
    }, 500);

    if (name === 'start_date') this.getFormControl(index, 'end_date').updateValueAndValidity();
    if (name === 'end_date') this.getFormControl(index, 'start_date').updateValueAndValidity();

    if (name === 'country') {
      this.getFormControl(index, name).setValue(option.val);
    }

    if (name === 'education_level') {
      this.getFormControl(index, 'education_level').setValue(option.val);
    }

    if (name === 'education_name') {
      this.getFormControl(index, name).setValue(option.text);
      this.searchForStudy(option.text, (data) => {
        const dataEducation = data.data.find((item) => item.id === option.id);

        if (this.levelOptions[index] === undefined) {
          this.levelOptions[index] = [...this.hardcodedLevelOptions];
        } else {
          this.levelOptions[index] = [...this.levelOptions[index]];
        }

        if (dataEducation?.education_level != null) {
          this.getFormControl(index, 'education_level').setValue(String(dataEducation.education_level));
        }

        setTimeout(() => {
          this.form.updateValueAndValidity();
        }, 0);
      });
    }
  }

  handleValueChanged(event, index, options, name) {
    if (name === 'school_name') {
      this.getFormControl(index, name).setValue(event);
    }

    if (name === 'country') {
      if (options && event) {
        const optionIndex = options.findIndex((item) => item.text === event);
        if (optionIndex !== -1) {
          this.getFormControl(index, name).setValue(options[optionIndex].val);
        } else {
          this.getFormControl(index, name).setValue('');
        }
      }
    }

    if (name === 'education_name') {
      this.getFormControl(index, name).setValue(event);

      this.searchForStudy(event, (data) => {
        this.studyOptions[index] = createOptions(data.data);
      });
    }

    if (name === 'education_level') {
      if (options && event) {
        const optionIndex = options.findIndex((item) => item.text === event);
        if (optionIndex !== -1) {
          this.getFormControl(index, 'education_level').setValue(options[optionIndex].val);
        } else {
          this.getFormControl(index, 'education_level').setValue('');
        }
      }

      this.form.updateValueAndValidity();
    }
  }

  handleInputFocused(index, options, name) {
    if (name === 'education_level') {
      this.levelOptions[index] = [...this.hardcodedLevelOptions];
    }

    if (name === 'education_name') {
      if (!options[index] || options[index].length === 0) {
        this.searchForStudy('', (data) => {
          this.studyOptions[index] = createOptions(data.data);
        });
      }
    }
  }

  changePositionInJobsList(event: CdkDragDrop<any[]>) {
    if (event.previousContainer !== event.container) return;
    if (event.previousIndex === event.currentIndex) return;

    const controls = [...this.formSections.controls];
    moveItemInArray(controls, event.previousIndex, event.currentIndex);
    while (this.formSections.length) {
      this.formSections.removeAt(0, { emitEvent: false });
    }
    console.log(controls);
    controls.forEach((c) => this.formSections.push(c, { emitEvent: false }));
    this.resetOrderProperty();
    this.formSections.updateValueAndValidity();
  }

  resetOrderProperty() {
    for (let i = 0; i < this.formSections.controls.length; i++) {
      this.formSections.controls[i].get('order')?.setValue(i, { emitEvent: false });
    }
  }

  editJob(index: number) {
    this.exp_index = this.exp_index === index ? undefined : index;

    if (this.exp_index !== index) {
      setTimeout(() => (this.exp_opened = false), 350);
    } else {
      this.exp_opened = true;
    }
  }

  stillStudingChange(checked: boolean, index, controlName): void {
    if (checked) {
      this.getFormControl(index, controlName).setValue('');
    }
  }

  private get hardcodedLevelOptions(): Option[] {
    return SELECTABLE_EDUCATION_LEVELS.map((level) => ({
      id: String(level),
      text: this.translateService.instant(EDUCATION_LEVEL_LABEL_KEYS[level]),
      val: String(level),
    }));
  }

  searchForStudy(seachValue, callback: (data) => void) {
    this.educationService.getEducation(seachValue).subscribe(
      (data) => {
        callback(data);
      },
      (err) => {
        console.error('Error getting study ', err);
      }
    );
  }

  removeItem(index: any) {
    this.formSections.removeAt(index);
    this.checkSectionCount();
  }

  checkSectionCount() {
    const numSections = this.formSections.length;
    this.isAddEducationDisabled = numSections >= this.maxEducationSteps;
  }

  getFormControl(index: number, controlName: string): FormControl {
    const section = this.formSections.at(index) as FormGroup;
    return section.get(controlName) as FormControl;
  }

  getFormArray(index: number, controlName: string): FormArray {
    const section = this.formSections.at(index) as FormGroup;
    return section.get(controlName) as FormArray;
  }

  get formSections() {
    return this.form.get('formSections') as FormArray;
  }

  removeField(i, name, fieldInd) {
    const formArray = this.getFormArray(i, name);
    formArray.removeAt(fieldInd);
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  validityChecking(index, status) {
    if (status === 'INVALID') {
      return 'error';
    }

    const arrNot = [
      'education_options',
      'end_date',
      'education_name',
      'education_level',
      'id',
      'is_current',
      'level_options',
      'order',
    ];
    const job = this.formSections.at(index).value;
    const params = Object.keys(job);
    let isCurrentFilled = false;
    let endDateFilled = false;
    let emptyParams = [];

    for (let i = 0; i < params.length; i++) {
      const paramName = params[i];
      const paramValue = job[paramName];
      if (paramName === 'end_date' && paramValue !== '' && paramValue !== null) {
        endDateFilled = true;
      }
      if (paramName === 'is_current' && paramValue === true) {
        isCurrentFilled = true;
      }
      if (!arrNot.includes(paramName) && (paramValue === null || paramValue === '')) {
        emptyParams.push(paramName);
      }
    }

    if (emptyParams.length > 0 || (emptyParams.length === 0 && isCurrentFilled === false && endDateFilled === false)) {
      return 'warning';
    }

    return 'checked';
  }

  setMonthAndYear(date: Moment, i: number, controlName: string, datepicker: MatDatepicker<Moment>) {
    this.getFormControl(i, controlName).setValue(date.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'));
    datepicker.close();
  }
}
